import React from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { PurchaseDocDetail, PurchaseIntakeMessage } from "types/InvoiceInbox";
import { addPurchaseDocComment } from "services/invoice-inbox.service";
import { formatPrettyDate } from "utils/helperFunctions";
import InvoiceDocTrail from "./invoice-doc-trail";
import InvoiceDocSignatures from "./invoice-doc-signatures";
import { isPendingSignatureOf } from "./invoice-doc-signing";
import { Accordion, RunChange, buttonClass, inputClass } from "./invoice-doc-ui";

type Props = {
  companyId: string;
  detail: PurchaseDocDetail;
  busy: boolean;
  currentUserId: string | null;
  run: RunChange;
  reloadAfterChange: () => Promise<void>;
  /** The note being typed lives in the panel, so Escape does not close the
   * panel over an unsent note. */
  comment: string;
  setComment: (value: string) => void;
};

/** The rarely needed parts of a document, folded away until asked for:
 * who did what, signatures, notes, and what the sender's mail server proved. */
export default function InvoiceDocAccordions({
  companyId,
  detail,
  busy,
  currentUserId,
  run,
  reloadAfterChange,
  comment,
  setComment,
}: Props) {
  const { t } = useTranslation();
  const people = detail.people || {};
  const signatures = detail.signatures || [];
  const awaitingMySignature = signatures.some((row) => isPendingSignatureOf(row, currentUserId));

  return (
    <div className="divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white">
      {/* Both approval slots sit in the same list so each approver can see the other. */}
      <Accordion title={t("invoice_inbox_trail_title", { defaultValue: "Who did what" })}>
        <InvoiceDocTrail doc={detail.doc} approvals={detail.approvals} people={people} />
      </Accordion>

      {/* A signature the reader owes is a prompt, not an archive entry: the
          section opens by itself and its header says so. */}
      <Accordion
        title={t("invoice_inbox_signatures_title", { defaultValue: "Signatures" })}
        defaultOpen={awaitingMySignature}
        aside={
          awaitingMySignature ? (
            <span className="rounded bg-amber-50 px-1.5 py-0.5 text-amber-800">
              {t("invoice_inbox_signature_awaiting_me", { defaultValue: "Awaiting your signature" })}
            </span>
          ) : (
            signatures.length || undefined
          )
        }
      >
        <InvoiceDocSignatures
          companyId={companyId}
          doc={detail.doc}
          files={detail.files || []}
          signatures={signatures}
          fileStates={detail.fileStates || {}}
          signing={detail.signing}
          currentUserId={currentUserId}
          onChanged={reloadAfterChange}
        />
      </Accordion>

      <Accordion title={t("notes", { defaultValue: "Notes" })} aside={detail.comments.length}>
        <div className="mb-2 space-y-2">
          {detail.comments.map((c) => (
            <div key={c.id} className="border-l-2 pl-2 text-sm">
              <div>{c.body}</div>
              <div className="text-xs text-gray-500">
                {(people[c.user] && people[c.user].name) || "—"} · {formatPrettyDate(c.createdAt, "lt-LT", true)}
              </div>
            </div>
          ))}
          {!detail.comments.length && (
            <div className="text-sm text-gray-400">{t("invoice_inbox_no_notes", { defaultValue: "No notes yet." })}</div>
          )}
        </div>
        <textarea
          rows={2}
          aria-label={t("notes", { defaultValue: "Notes" })}
          className={inputClass}
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          placeholder={t("invoice_inbox_note_placeholder", { defaultValue: "Paid in advance, waiting for the delivery note…" })}
        />
        <button
          type="button"
          className={clsx(buttonClass, "mt-2")}
          disabled={busy || !comment.trim()}
          onClick={() =>
            run(async () => {
              await addPurchaseDocComment(companyId, detail.doc.id, { body: comment });
              setComment("");
            })
          }
        >
          {t("invoice_inbox_add_note", { defaultValue: "Add note" })}
        </button>
      </Accordion>

      {detail.message && <SenderAccordion message={detail.message} />}
    </div>
  );
}

type SenderVerdict = "passed" | "partly_checked" | "check" | "not_checked";

// Verdicts are nullable: null means NOT CHECKED, which must never be shown as
// a failure. Anything checked that is not a pass, or a Reply-To that differs
// from the sender, is worth a look. "Passed" needs BOTH checks to have run:
// one pass from a check the other half never confirmed proves too little to
// call the sender verified.
function senderVerdict(message: PurchaseIntakeMessage): SenderVerdict {
  const results = [message.dkimResult, message.spfSource === "header" ? message.spfResult : null].filter(Boolean);
  const replyToDiffers = Boolean(message.replyToAddress && message.replyToAddress !== message.fromAddress);
  if (replyToDiffers || results.some((result) => result !== "pass")) return "check";
  if (results.length === 2) return "passed";
  return results.length === 1 ? "partly_checked" : "not_checked";
}

const VERDICT_CHIP: Record<SenderVerdict, { key: string; defaultValue: string; className: string }> = {
  passed: { key: "invoice_inbox_sender_passed", defaultValue: "Verified", className: "bg-green-50 text-green-700" },
  partly_checked: {
    key: "invoice_inbox_sender_partly_checked",
    defaultValue: "Partly checked",
    className: "bg-gray-100 text-gray-600",
  },
  check: { key: "invoice_inbox_sender_check", defaultValue: "Check", className: "bg-amber-50 text-amber-800" },
  not_checked: { key: "not_checked", defaultValue: "not checked", className: "bg-gray-100 text-gray-600" },
};

function SenderAccordion({ message }: { message: PurchaseIntakeMessage }) {
  const { t } = useTranslation();
  const chip = VERDICT_CHIP[senderVerdict(message)];
  const notChecked = t("not_checked", { defaultValue: "not checked" });
  return (
    <Accordion
      title={t("invoice_inbox_sender_auth_title", { defaultValue: "Sender (DKIM/SPF)" })}
      aside={
        <span className={clsx("rounded px-1.5 py-0.5", chip.className)}>
          {t(chip.key, { defaultValue: chip.defaultValue })}
        </span>
      }
    >
      <div className="space-y-0.5 text-sm text-gray-600">
        <div className="font-mono">{message.fromAddress}</div>
        {message.replyToAddress && message.replyToAddress !== message.fromAddress && (
          <div className="text-amber-700">Reply-To: {message.replyToAddress}</div>
        )}
        <div>
          DKIM: {message.dkimResult || notChecked}
          {" · "}
          SPF: {message.spfSource === "header" ? message.spfResult : notChecked}
        </div>
      </div>
    </Accordion>
  );
}
